import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import Navbar from "../../components/ui/Navbar";
import styles from "./History.module.css";
import Footer from "../../components/ui/Footer";
import FixedAgent from "../../utils/api/FixedAgent";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Menu from "../../components/ui/Menu";

const getNormalizedIdentifier = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;

    const normalizedValue =
      typeof value === "string" ? value.trim() : String(value).trim();

    if (
      normalizedValue &&
      normalizedValue.toLowerCase() !== "null" &&
      normalizedValue.toLowerCase() !== "undefined"
    ) {
      return normalizedValue;
    }
  }

  return "";
};

function OrderDetails(props) {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [historyLength, setHistoryLength] = useState(0);
  const [agDownloadIdentifierTypes, setAgDownloadIdentifierTypes] = useState(
    {},
  );
  const [agCertificateSelector, setAgCertificateSelector] = useState({
    isOpen: false,
    product: null,
    identifierType: "policy_number",
  });
  const [fgiCertificateSelector, setFgiCertificateSelector] = useState({
    isOpen: false,
    product: null,
  });
  const { id } = useParams();
  const token = useSelector((state) => state.userData.loginData.token);
  const renewalPolicyReferences = useSelector(
    (state) => state.userData.renewalPolicyReferences,
  );

  const user = useSelector((state) => state.userData.personalInfo);

  // const accountType = useSelector(
  //   (state) => state.userData.loginData.account_type
  // );
  // const navigate = useNavigate();
  const url = process.env.REACT_APP__BASE_URL;
  const getAgProductKey = (product, index) =>
    product?.product_id || product?.headless?.manual_unique_id || String(index);

  const getAgPolicyNumber = (product) => {
    const orderPolicyReference = renewalPolicyReferences?.[id];

    return getNormalizedIdentifier(
      product?.policy_number,
      product?.headless?.policy_number,
      products?.order?.[0]?.policy_number,
      orderPolicyReference?.policyNumber,
    );
  };

  const getAgPlateNumber = (product) => {
    const orderPlateReference = renewalPolicyReferences?.[id];

    return getNormalizedIdentifier(
      product?.plate_no,
      product?.plate_number,
      product?.headless?.plate_no,
      product?.headless?.plate_number,
      products?.order?.[0]?.plate_details?.plate_no,
      products?.order?.[0]?.plate_no,
      orderPlateReference?.plateNumber,
    );
  };

  const getDefaultAgIdentifierType = (product) =>
    getAgPolicyNumber(product) ? "policy_number" : "plate_no";

  const isAgPrintPlatform = (platform) =>
    platform === "ag" || platform === "agp";
  const isFgiPrintPlatform = (platform) =>
    platform === "fgi" || platform === "fgip";

  // The current AG print endpoint returns the opposite document for these
  // table values, so these map the user-facing choices to its actual output.
  const agCertificateTables = {
    coloured: 2,
    plain: 1,
  };

  const fgiCertificateTypes = {
    plain: "plain",
    preprinted: "preprinted",
  };

  const getPolicyDownloadUrl = (product, options = {}) => {
    const platform = product?.headless?.platform?.toLowerCase();
    const plateNo = products?.order?.[0]?.plate_details?.plate_no;
    const agPolicyNumber = getAgPolicyNumber(product);
    const agPlateNumber = getAgPlateNumber(product);
    const agTable = options.table || 1;
    const agIdentifierType = options.identifierType || "policy_number";
    const fgiCertificateType = options.certificateType || "plain";

    if (product?.file_link) return product.file_link;
    if (!platform) return "";

    if (platform === "cei") {
      if (!plateNo) return "";
      return `https://certs.ceiil.com.ng/Certificate/GetPlainPolicyDocumentByRegistrationNumber/${plateNo}`;
    }

    if (platform === "nicon") {
      if (!plateNo) return "";
      return `${url}/app/fixedagent/customer/order/nicon/cert/${plateNo}`;
    }

    if (platform === "iei") {
      if (!plateNo) return "";
      return `${url}/app/fixedagent/customer/order/ieiplc/download/${plateNo}`;
    }

    if (isAgPrintPlatform(platform)) {
      if (agIdentifierType === "policy_number") {
        if (!agPolicyNumber) return "";
        return `${url}/app/fixedagent/ag-insurance/print?policy_number=${encodeURIComponent(
          agPolicyNumber,
        )}&table=${agTable}`;
      }

      if (!agPlateNumber) return "";
      return `${url}/app/fixedagent/ag-insurance/print?plate_no=${encodeURIComponent(
        agPlateNumber,
      )}&table=${agTable}`;
    }

    if (isFgiPrintPlatform(platform)) {
      const fgiPlateNumber = getAgPlateNumber(product);
      if (!fgiPlateNumber) return "";
      return `${url}/app/fixedagent/fgi-insurance/print?plate_no=${encodeURIComponent(
        fgiPlateNumber,
      )}&type=${fgiCertificateType}`;
    }

    return "";
  };

  const closeAgCertificateSelector = () => {
    setAgCertificateSelector({
      isOpen: false,
      product: null,
      identifierType: "policy_number",
    });
  };

  const closeFgiCertificateSelector = () => {
    setFgiCertificateSelector({ isOpen: false, product: null });
  };

  const handleAgIdentifierTypeChange = (productKey, value) => {
    setAgDownloadIdentifierTypes((prev) => ({
      ...prev,
      [productKey]: value,
    }));
  };

  const openPolicyDownload = (product, identifierType = "policy_number") => {
    const platform = product?.headless?.platform?.toLowerCase();

    if (isAgPrintPlatform(platform)) {
      const downloadUrl = getPolicyDownloadUrl(product, { identifierType });
      if (!downloadUrl) return;

      setAgCertificateSelector({
        isOpen: true,
        product,
        identifierType,
      });
      return;
    }

    if (isFgiPrintPlatform(platform)) {
      const downloadUrl = getPolicyDownloadUrl(product, {
        certificateType: "plain",
      });
      if (!downloadUrl) return;

      setFgiCertificateSelector({ isOpen: true, product });
      return;
    }

    const downloadUrl = getPolicyDownloadUrl(product);
    if (downloadUrl) {
      window.open(downloadUrl, "_blank");
    }
  };

  const handleAgCertificateSelect = (table) => {
    if (!agCertificateSelector.product) return;

    const downloadUrl = getPolicyDownloadUrl(agCertificateSelector.product, {
      table,
      identifierType: agCertificateSelector.identifierType,
    });

    if (downloadUrl) {
      window.open(downloadUrl, "_blank");
    }

    closeAgCertificateSelector();
  };

  const handleFgiCertificateSelect = (certificateType) => {
    if (!fgiCertificateSelector.product) return;

    const downloadUrl = getPolicyDownloadUrl(fgiCertificateSelector.product, {
      certificateType,
    });

    if (downloadUrl) window.open(downloadUrl, "_blank");

    closeFgiCertificateSelector();
  };

  const closeMenuHandler = () => {
    props.setShowMenu(false);
  };

  useEffect(() => {
    if (historyLength % 30 === 0 && historyLength !== 0) {
      FixedAgent.sendTicket({
        to_name: "Admin",
        from_name: `${user.fullname} (${user.email})`,
        message: `Congratulations! ${user.fullname} (${user.email}) has successfully processed 30 orders and has won a FREE PIN!`,
      });
    }
  }, [historyLength, user.fullname, user.email]);

  useEffect(() => {
    const fetchHistoryList = async () => {
      try {
        const historyListRes = await FixedAgent.getManualOrderHistory({
          token,
        });
        setHistoryLength(historyListRes.data.data.length);
      } catch (err) {
        toast.error(err?.response?.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
      }
    };
    fetchHistoryList();
  }, [token]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const orderRes = await FixedAgent.getManualOrderDetails({
          id,
          token,
        });
        setProducts(orderRes.data);
      } catch (err) {
        setIsLoading(false);
        toast.error(err?.response?.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
      }
    };
    fetchOrderDetails();
  }, [id, token]);

  return (
    <div>
      <ToastContainer />
      <div className="menu" onClick={closeMenuHandler}>
        {props.showMenu && <Menu setShowMenu={props.setShowMenu} />}
      </div>
      {agCertificateSelector.isOpen && (
        <div
          className={styles.modal_overlay}
          onClick={closeAgCertificateSelector}
        >
          <div
            className={styles.modal_card}
            onClick={(e) => e.stopPropagation()}
          >
            <h1>Select Certificate Type</h1>
            <p>
              Choose the AG certificate format you want to print for this
              policy.
            </p>
            <button
              className={styles.modal_button}
              onClick={() =>
                handleAgCertificateSelect(agCertificateTables.coloured)
              }
            >
              Coloured Certificate
            </button>
            <button
              className={styles.modal_button_secondary}
              onClick={() =>
                handleAgCertificateSelect(agCertificateTables.plain)
              }
            >
              Plain Certificate (A5)
            </button>
            <button
              className={styles.modal_cancel_button}
              onClick={closeAgCertificateSelector}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {fgiCertificateSelector.isOpen && (
        <div
          className={styles.modal_overlay}
          onClick={closeFgiCertificateSelector}
        >
          <div
            className={styles.modal_card}
            onClick={(e) => e.stopPropagation()}
          >
            <h1>Select Certificate Type</h1>
            <p>Choose the FGI certificate format you want to download.</p>
            <button
              className={styles.modal_button}
              onClick={() =>
                handleFgiCertificateSelect(fgiCertificateTypes.plain)
              }
            >
              Plain Certificate
            </button>
            <button
              className={styles.modal_button_secondary}
              onClick={() =>
                handleFgiCertificateSelect(fgiCertificateTypes.preprinted)
              }
            >
              Preprinted Certificate
            </button>
            <button
              className={styles.modal_cancel_button}
              onClick={closeFgiCertificateSelector}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <Navbar title="Order Details" setShowMenu={props.setShowMenu} />
      <div className={styles.body}>
        <div
          style={{ display: "flex", flexDirection: "column" }}
          className={styles.body_header}
        >
          <h1>
            Total: NGN
            {products?.order?.length >= 0
              ? products.order?.length > 0
                ? Number(products.order[0].amount).toLocaleString()
                : "0"
              : "0"}
          </h1>

          <h1>
            Plate Number:{" "}
            {products?.order?.length > 0
              ? products.order[0].plate_details.plate_no
              : "N/A"}
          </h1>
          <div className={styles.cards_wrapper}>
            {products?.details?.map((product, index) => (
              (() => {
                const platform = product?.headless?.platform?.toLowerCase();
                const isAg = isAgPrintPlatform(platform);
                const isFgi = isFgiPrintPlatform(platform);
                const productKey = getAgProductKey(product, index);
                const selectedAgIdentifierType =
                  agDownloadIdentifierTypes[productKey] ||
                  getDefaultAgIdentifierType(product);
                const downloadUrl = getPolicyDownloadUrl(product, {
                  identifierType: selectedAgIdentifierType,
                });

                return (
                  <div className={styles.card} key={product?.product_id}>
                    <h1 className={styles.card_title}>
                      {product?.product_details?.product_name}
                    </h1>
                    <p className={styles.card_description}>
                      {product?.product_details?.product_category}
                      <span>{product?.product_details?.product_type}</span>
                    </p>
                {/* <span className={styles.card_amount}>
                  NGN {Number(product?.price)?.toLocaleString()}
                </span> */}

                    {isAg ? (
                      <div className={styles.card_action_row}>
                        <button
                          disabled={!downloadUrl}
                          onClick={() =>
                            openPolicyDownload(product, selectedAgIdentifierType)
                          }
                          className={styles.card_button}
                        >
                          Download Plain Policy
                        </button>
                        <select
                          className={styles.card_select}
                          value={selectedAgIdentifierType}
                          onChange={(e) =>
                            handleAgIdentifierTypeChange(
                              productKey,
                              e.target.value,
                            )
                          }
                        >
                          <option value="policy_number">Policy Number</option>
                          <option value="plate_no">Plate Number</option>
                        </select>
                      </div>
                    ) : (
                      <button
                        disabled={
                          isFgi
                            ? !getPolicyDownloadUrl(product, {
                                certificateType: "plain",
                              })
                            : !downloadUrl
                        }
                        onClick={() => openPolicyDownload(product)}
                        className={styles.card_button}
                      >
                        {isFgi ? "Download Certificate" : "Download Plain Policy"}
                      </button>
                    )}

                    {isAg && !downloadUrl && (
                      <p className={styles.card_description}>
                        Selected AG option is not available yet
                      </p>
                    )}
                    {isFgi &&
                      !getPolicyDownloadUrl(product, {
                        certificateType: "plain",
                      }) && (
                        <p className={styles.card_description}>
                          FGI certificate is not available yet
                        </p>
                      )}

                    {platform === "cei" && (
                      <button
                        onClick={() =>
                          window.open(
                            `https://certs.ceiil.com.ng/Certificate/GetPrePrintedPolicyDocumentByRegistrationNumber/${products?.order[0]?.plate_details?.plate_no}`,
                            "_blank"
                          )
                        }
                        className={styles.card_button}
                      >
                        Download Pdf Policy
                      </button>
                    )}
                {/* {accountType !== "DISTRIBUTOR" &&
                  product?.headless?.platform?.toLowerCase() === "cei" && (
                    <p
                      onClick={() =>
                        navigate(
                          `/edit-order/${product?.headless?.manual_unique_id}`
                        )
                      }
                      style={{ cursor: "pointer", color: "blue" }}
                    >
                      Edit this order
                    </p>
                  )} */}
                  </div>
                );
              })()
            ))}

            {isLoading &&
              [...Array(3)].map((_, index) => (
                <div key={index}>
                  <div className={`${styles.card} ${styles.skeleton}`}></div>
                </div>
              ))}
          </div>
          <div
            className={styles.card}
            style={{
              textAlign: "center",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#FFFFFF",
              boxShadow: "0px 4px 4px 0px #00000040",
              height: "304px",
              gap: "20px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p
              style={{
                fontFamily: "Circular Std",
                fontWeight: "700",
                fontSize: "18px",
                lineHeight: "100%",
                color: "black",
                letterSpacing: "0px",
                textAlign: "center",
              }}
            >
              {(() => {
                if (historyLength % 30 === 0 && historyLength !== 0) {
                  return "Congratulations";
                } else if (
                  historyLength % 30 >= 25 &&
                  historyLength % 30 < 30 &&
                  historyLength !== 0
                ) {
                  return "Almost!";
                } else {
                  return "Well done!";
                }
              })()}
            </p>
            <img
              src={(() => {
                if (historyLength % 30 === 0 && historyLength !== 0) {
                  return require("../../assets/image 82.png");
                } else if (
                  historyLength % 30 >= 25 &&
                  historyLength % 30 < 30 &&
                  historyLength !== 0
                ) {
                  return require("../../assets/image 84.png");
                } else {
                  return require("../../assets/image 83.png");
                }
              })()}
              alt="Dynamic"
              style={{ height: "131px", width: "50%", objectFit: "contain" }}
            />
            <p
              style={{
                fontFamily: "Circular Std",
                fontWeight: "700",
                color: "black",
                fontSize: "18px",
                lineHeight: "100%",
                letterSpacing: "0px",
                textAlign: "center",
              }}
            >
              {(() => {
                if (historyLength % 30 === 0 && historyLength !== 0) {
                  return "Congratulations! You have won a FREE PIN.";
                } else if (
                  historyLength % 30 >= 25 &&
                  historyLength % 30 < 30 &&
                  historyLength !== 0
                ) {
                  return `${30 - (historyLength % 30)} more to get a FREE PIN.`;
                } else {
                  return `Renew ${
                    30 - (historyLength % 30)
                  } more to get a FREE PIN.`;
                }
              })()}
            </p>
            <p
              style={{
                fontFamily: "Circular Std",
                fontWeight: "700",
                color: "black",
                fontSize: "12px", // Made text smaller
                lineHeight: "100%",
                letterSpacing: "0px",
                textAlign: "center", // Aligned text to the right
              }}
            >
              PS: This applies to only NICON. Also, you are eligible to 2 free
              pins only.
            </p>
          </div>
        </div>{" "}
      </div>

      <Footer />
    </div>
  );
}

export default OrderDetails;
